import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';

import {
  IConversation,
  IConversationPageData,
  IConversationReference,
  IConversationService,
  IMessage,
} from 'services/conversationService';
import { IConversationModel } from 'pages/Conversations';

const useConversations = (
  customerId: string | number,
  conversationId: string | number,
  conversationService: IConversationService
) => {
  let [errorMessage, setErrorMessage] = useState('');

  const queryClient = useQueryClient();
  var conversationCacheKey = 'Conversation ' + conversationId;
  var conversationsDataCacheKey = 'Conversations for ' + customerId;

  const loadConversation = () => {
    setErrorMessage('');
    if (conversationQuery.isIdle && !conversationQuery.isSuccess) conversationQuery.refetch();
  };

  const addMessageToConversation = (
    form: IConversationModel,
    callback: (message: IMessage) => void
  ) => {
    setErrorMessage('');
    messageMutation.mutate(form, {
      onSuccess(data, variables, context) {
        callback(data);
      },
      onError(error: any, variables, context) {
        setErrorMessage(error.toString());
        console.debug('error', error);
        console.debug('message', variables);
        console.debug('context', context);
      },
    });
  };

  const createConversation = (form: IConversationModel, callback: () => void) => {
    setErrorMessage('');
    conversationMutation.mutate(form, {
      onSuccess(data, variables, context) {
        callback();
      },
    });
  };

  const toggleArchivingStatus = () => {
    setErrorMessage('');
    archivingMutation.mutate();
  };

  var messageMutation = useMutation((form: IConversationModel) => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    var temporaryMessage: IMessage & { _tempId?: string } = {
      _tempId: tempId,
      author: '',
      authorIsCurrentUser: true,
      creationDate: new Date(),
      message: form.message,
      attachments: (!!form.attachments ? form.attachments : []).map((a) => {
        return {
          fileName: a.name,
          filePath: '',
        };
      }),
    };

    var conversation = queryClient.getQueryData<IConversation>(conversationCacheKey);
    if (conversation) {
      conversation = { ...conversation, messages: [temporaryMessage, ...conversation.messages] };
      queryClient.setQueryData(conversationCacheKey, conversation);
    }

    return conversationService
      .addMessageToConversation(
        {
          messageBody: form.message,
          conversationId: conversationId.toString() || '',
        },
        form.attachments
      )
      .then((message: IMessage) => {
        var conversation = queryClient.getQueryData<IConversation>(conversationCacheKey);
        if (conversation) {
          const filteredMessages = conversation.messages.filter(
            (m: IMessage & { _tempId?: string }) => m._tempId !== tempId
          );
          conversation = {
            ...conversation,
            messages: [message, ...filteredMessages],
          };
          queryClient.setQueryData(conversationCacheKey, conversation);
        }
        return message;
      });
  });

  var archivingMutation = useMutation(conversationCacheKey, () => {
    var refToBackup: IConversationReference;
    var refToCurrentConversationInList: IConversationReference;
    var conversation = queryClient.getQueryData<IConversation>(conversationCacheKey);
    if (conversation) {
      var archivingOperation = conversation.isArchived
        ? conversationService.unarchiveConversation
        : conversationService.archiveConversation;
      var pageData = queryClient.getQueryData<IConversationPageData>(conversationsDataCacheKey);

      queryClient.setQueryData<IConversation>(conversationCacheKey, {
        ...conversation,
        isArchived: !conversation.isArchived,
      });

      if (pageData?.conversations) {
        pageData.conversations = pageData.conversations.map((c) => {
          if (c.conversationId !== conversation?.conversationId) {
            return c;
          } else {
            refToBackup = c;
            refToCurrentConversationInList = { ...c, isArchived: !c.isArchived };
            return refToCurrentConversationInList;
          }
        });
        queryClient.setQueryData<IConversationPageData>(conversationsDataCacheKey, {
          ...pageData,
          conversations: pageData?.conversations,
        });
      }
      return archivingOperation(conversationId.toString()).catch((error: any) => {
        Sentry.captureException(error);
        var pageData = queryClient.getQueryData<IConversationPageData>(conversationsDataCacheKey);
        if (pageData?.conversations) {
          pageData.conversations.map((c) =>
            c === refToCurrentConversationInList ? refToBackup : c
          );
          queryClient.setQueryData<IConversationPageData>(conversationsDataCacheKey, {
            ...pageData,
            conversations: [...pageData?.conversations],
          });
        }
      });
    }

    return Promise.resolve();
  });

  const conversationMutation = useMutation(conversationCacheKey, (form: IConversationModel) => {
    const tempConversationId = `temp-${Date.now()}-${Math.random()}`;
    var conversationPageData =
      queryClient.getQueryData<IConversationPageData>(conversationsDataCacheKey);
    var temporaryConversationReference: IConversationReference;

    if (conversationPageData) {
      temporaryConversationReference = {
        conversationId: tempConversationId,
        hasUnreadMessages: true,
        creationDate: dayjs(new Date()).format('LLL'),
        customerId: customerId.toLocaleString(),
        isArchived: false,
        mostRecentActivityDate: dayjs(new Date()).format('LLL'),
        otherParty: [
          {
            conversationId: tempConversationId,
            displayName: form.recipient.displayName,
            email: form.recipient.email,
          },
        ],
        subject: form.subject,
      };
      queryClient.setQueryData(conversationsDataCacheKey, {
        recipients: conversationPageData.recipients,
        conversations: [temporaryConversationReference, ...conversationPageData.conversations],
      });
    }

    return conversationService
      .createNewConversation(customerId.toString(), { ...form }, form.attachments)
      .then((c: IConversation) => {
        var currentPageData =
          queryClient.getQueryData<IConversationPageData>(conversationsDataCacheKey);
        if (currentPageData) {
          const filteredConversations = currentPageData.conversations.filter(
            (conv) => conv.conversationId !== tempConversationId
          );
          queryClient.setQueryData(conversationsDataCacheKey, {
            recipients: currentPageData.recipients,
            conversations: [
              {
                ...temporaryConversationReference,
                conversationId: c.conversationId,
              },
              ...filteredConversations,
            ],
          });
        }
        return c;
      })
      .catch((error: any) => {
        Sentry.captureException(error);
        console.error('failed to create conversation');
        var currentPageData =
          queryClient.getQueryData<IConversationPageData>(conversationsDataCacheKey);
        if (currentPageData) {
          const filteredConversations = currentPageData.conversations.filter(
            (conv) => conv.conversationId !== tempConversationId
          );
          queryClient.setQueryData(conversationsDataCacheKey, {
            recipients: currentPageData.recipients,
            conversations: filteredConversations,
          });
        }
      });
  });

  const conversationDataQuery = useQuery<IConversationPageData, any>(
    conversationsDataCacheKey,
    () => {
      if (!customerId || Number(customerId) < 1)
        return new Promise<IConversationPageData>(() => {});
      else return conversationService.loadConversationsData(customerId.toString());
    },
    {
      refetchOnWindowFocus(query) {
        return false;
      },
      select: (data) => {
        return data;
      },
    }
  );

  const conversationQuery = useQuery<IConversation>(
    conversationCacheKey,
    () => {
      if (!customerId || conversationId === '-1') return new Promise<IConversation>(() => {});
      else return conversationService.loadConversation(conversationId.toString());
    },
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onError: (error: any) => {
        setErrorMessage('conversation.403');
        console.error('Conversation load failed');
        throw error;
      },
    }
  );

  return {
    conversations: conversationDataQuery.data?.conversations || [],
    selectedConversation: conversationQuery.data,
    recipients: conversationDataQuery.data?.recipients || [],
    conversationsAreLoading: conversationDataQuery.isFetching || customerId == -1,
    loadConversation,
    conversationIsLoading: conversationQuery.isLoading,
    addMessageToConversation,
    isSubmitting: messageMutation.isLoading,
    createConversation,
    toggleArchivingStatus,
    errorMessage,
  };
};

export default useConversations;
